import * as React from 'react';
import PropTypes from 'prop-types';
import CssBaseline from '@material-ui/core/CssBaseline';
import { createTheme } from '@material-ui/core';
import merge from 'lodash/merge';
import ConnectedThemeProvider from '@configuratorware/configurator-frontendgui/App/Screens/Configurator/ThemeProvider';
import { XXL_COLORS } from './theme/colors';
import { ARTICULAT_FONT_FAMILY, SPACE_GROTESK_FONT_FAMILY } from './theme/fonts';

const fontFamily = ARTICULAT_FONT_FAMILY;
const borderRadius = 0;
const shadowPaper = '0 1px 1px rgba(0,0,0,0.04)';
// Hero/CTA device: a confident, solid black offset — reserved for primary actions only.
const shadowButton = '4px 4px 0 #0c0c0c';
const shadowButtonHover = '6px 6px 0 #0c0c0c';
// Primary button (MainButton, MuiButton.containedPrimary) is flat by default;
// this offset shadow only appears on hover, alongside the black→blue fill flip.
const primaryButtonHoverShadow = '4px 4px 0 rgba(12,12,12,0.9)';

const customTheme = createTheme({
  palette: {
    primary: {
      main: XXL_COLORS.primary,
      dark: XXL_COLORS.primaryDark,
      contrastText: XXL_COLORS.primaryContrast,
    },
    secondary: {
      main: XXL_COLORS.accent,
      dark: XXL_COLORS.accentDark,
      contrastText: '#000000',
    },
    background: {
      default: XXL_COLORS.pageBackground,
      paper: XXL_COLORS.paperBackground,
    },
    text: {
      primary: XXL_COLORS.sectionTitle,
      secondary: XXL_COLORS.textBase,
    },
    divider: XXL_COLORS.borderDefault,
  },
  typography: {
    fontFamily,
    fontSize: 14,
    fontWeightMedium: 700,
    fontWeightBold: 700,
    h4: { fontWeight: 700, letterSpacing: '-0.01em', color: XXL_COLORS.sectionTitle },
    h5: { fontWeight: 700, letterSpacing: '-0.01em', color: XXL_COLORS.sectionTitle },
    h6: { fontWeight: 700, letterSpacing: '0', color: XXL_COLORS.sectionTitle },
    subtitle1: { fontWeight: 700, color: XXL_COLORS.sectionTitle },
    subtitle2: { fontWeight: 700, color: XXL_COLORS.sectionTitle },
    body1: { fontSize: 14, lineHeight: 1.5, color: XXL_COLORS.textBase },
    body2: { fontSize: 13, lineHeight: 1.5, color: XXL_COLORS.textBase },
    caption: { fontSize: 12, color: XXL_COLORS.textBase },
    button: {
      textTransform: 'uppercase',
      fontWeight: 700,
      fontSize: 14,
      letterSpacing: '0.04em',
    },
  },
  shape: { borderRadius },
});

const overrideTheme = (theme) => {
  theme.palette.primary = {
    main: XXL_COLORS.primary,
    dark: XXL_COLORS.primaryDark,
    contrastText: XXL_COLORS.primaryContrast,
  };
  theme.palette.text = {
    primary: XXL_COLORS.sectionTitle,
    secondary: XXL_COLORS.textBase,
  };
  theme.typography = { ...theme.typography, ...customTheme.typography };
  theme.shape = { ...theme.shape, borderRadius };

  theme.xxlprint = {
    ...XXL_COLORS,
    borderRadius,
    fontFamily,
    shadowPaper,
    shadowButton,
    shadowButtonHover,
  };

  // boxed={false} renders cardUnboxed, not card — both need the same mobile stack layout.
  const amountPriceMobileCardXs = {
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: theme.spacing(2),
      padding: 0,
      overflow: 'visible',
      '& > .MuiGrid-item': { flexBasis: '100%', maxWidth: '100%', width: '100%' },
    },
  };

  merge(theme.overrides, {
    MuiCssBaseline: {
      '@global': {
        'html, body': {
          overflow: 'hidden',
          height: '100%',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
        },
        '#root': {
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
        },
        // Brand focus ring — keyboard navigation only, in cyan.
        ':focus-visible': {
          outline: `2px solid ${XXL_COLORS.accent}`,
          outlineOffset: '2px',
        },
        '@keyframes brandSpin': {
          to: { transform: 'rotate(360deg)' },
        },
        '.MuiButton-root.custom-css-cta-button': {
          backgroundColor: `${XXL_COLORS.primary} !important`,
          color: `${XXL_COLORS.primaryContrast} !important`,
          fontFamily: SPACE_GROTESK_FONT_FAMILY,
          border: `1px solid ${XXL_COLORS.primary} !important`,
          // !important needed here (unlike MainButton/MuiButton below) since this is a
          // hand-written global CSS rule competing with other !important global rules,
          // not a JSS override relying on normal specificity.
          boxShadow: 'none !important',
        },
        '.MuiButton-root.custom-css-cta-button:hover': {
          backgroundColor: `${XXL_COLORS.accent} !important`,
          boxShadow: `${primaryButtonHoverShadow} !important`,
        },
        '.custom-css-cta-button .MuiButton-label, .custom-css-cta-button .MuiButton-label *': {
          color: `${XXL_COLORS.primaryContrast} !important`,
        },
        '.custom-css-cta-button:hover .MuiButton-label, .custom-css-cta-button:hover .MuiButton-label *': {
          color: `${XXL_COLORS.primary} !important`,
        },
        '.MuiPaper-root.custom-css-cta-box': {
          backgroundColor: 'transparent !important',
          boxShadow: 'none !important',
          border: 'none !important',
        },
      },
    },
    // Re-skin the core configuratorware loading overlay (GlobalLoader → Progress) so the
    // generic Material spinner becomes our branded spinner wherever the core shows it
    // (initial bootstrap, visualization load, item overview, etc.).
    Progress: {
      container: {
        backgroundColor: 'rgba(246,246,246,0.7) !important',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1300,
      },
      inner: {
        flex: 'none',
        // Hide the stock MUI CircularProgress…
        '& .MuiCircularProgress-root': { display: 'none' },
        // …and render our border-spinner in its place.
        '&::after': {
          content: '""',
          display: 'block',
          width: 44,
          height: 44,
          borderRadius: '50%',
          border: `3px solid ${XXL_COLORS.borderDefault}`,
          borderTopColor: XXL_COLORS.borderStrong,
          animation: 'brandSpin 0.8s linear infinite',
        },
      },
    },
    // Product overview screen — re-skin its named makeStyles to our system.
    ItemOverview: {
      itemContainer: {
        // Drop the stock grey rounded panel; let the cards sit on the page.
        backgroundColor: 'transparent',
        borderRadius: 0,
        padding: theme.spacing(2, 0),
      },
      categoryButton: {
        backgroundColor: XXL_COLORS.paperBackground,
        color: XXL_COLORS.sectionTitle,
        border: `1px solid ${XXL_COLORS.borderStrong}`,
        borderRadius: 0,
        padding: theme.spacing(1.25, 2.5),
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        '&.active': {
          backgroundColor: XXL_COLORS.accent,
          color: XXL_COLORS.surfaceDark,
        },
      },
    },
    ItemTile: {
      root: {
        width: 'auto',
        height: 'auto',
        minHeight: 300,
        margin: theme.spacing(1),
        backgroundColor: XXL_COLORS.paperBackground,
        border: `1px solid ${XXL_COLORS.borderStrong}`,
        borderRadius: 0,
        boxShadow: 'none',
      },
      image: {
        width: '100%',
      },
      buttonContainer: {
        width: '100%',
        // CTA fills the tile so the long "KONFIGURIEREN" label never clips.
        // boxShadow is intentionally NOT set here — MainButton.root already
        // provides the flat-by-default/shadow-on-hover behavior; forcing it
        // here (as before) fought that with a higher-specificity !important
        // rule and left the shadow visible at all times.
        '& .MuiButtonBase-root': {
          width: '100%',
        },
      },
    },
    CreatorAlignTarget: {
      root: {
        display: 'none',
      },
    },
    MuiAppBar: {
      root: {
        backgroundColor: `${XXL_COLORS.surfaceDark} !important`,
        color: `${XXL_COLORS.textOnDark} !important`,
        '& .MuiTypography-root': {
          color: XXL_COLORS.textOnDark,
        },
        '& .MuiIconButton-root': {
          color: XXL_COLORS.accent,
          '&:hover': {
            color: XXL_COLORS.accentDark,
            // rgba equivalent of XXL_COLORS.accent (#7dd3fc) at 12% opacity.
            backgroundColor: 'rgba(125,211,252,0.12)',
          },
        },
        '& .MuiSvgIcon-root': {
          color: XXL_COLORS.accent,
        },
        // Header tool icons are Material font icons (.MuiIcon-root) whose color
        // the core ToolbarList sets explicitly — override to the brand cyan.
        '& .MuiIcon-root, & .material-icons': {
          color: `${XXL_COLORS.accent} !important`,
        },
        '& button:hover .MuiIcon-root, & button:hover .material-icons': {
          color: `${XXL_COLORS.accentDark} !important`,
        },
      },
      colorDefault: {
        backgroundColor: `${XXL_COLORS.surfaceDark} !important`,
        color: `${XXL_COLORS.textOnDark} !important`,
      },
    },
    VariantChooser: {
      typographyBody: {
        fontSize: 22,
        fontWeight: 900,
        letterSpacing: '-0.03em',
        // 1.1 clipped descenders (e.g. "g") since the container's overflow:hidden
        // box is exactly one line-height tall — 1.3 gives enough headroom.
        lineHeight: 1.3,
      },
    },
    MainButton: {
      root: {
        backgroundColor: `${XXL_COLORS.primary} !important`,
        color: `${XXL_COLORS.primaryContrast} !important`,
        fontFamily: SPACE_GROTESK_FONT_FAMILY,
        textTransform: 'uppercase !important',
        letterSpacing: '0.04em',
        border: `1px solid ${XXL_COLORS.primary} !important`,
        boxShadow: 'none',
        '&:hover': {
          backgroundColor: `${XXL_COLORS.accent} !important`,
          color: `${XXL_COLORS.primary} !important`,
          boxShadow: primaryButtonHoverShadow,
        },
      },
      label: {
        textTransform: 'uppercase !important',
        '&::after': {
          content: '"\\2192"',
          marginLeft: '0.7em',
          fontWeight: 700,
        },
      },
    },
    MuiButton: {
      containedPrimary: {
        backgroundColor: `${XXL_COLORS.primary} !important`,
        color: `${XXL_COLORS.primaryContrast} !important`,
        fontFamily: SPACE_GROTESK_FONT_FAMILY,
        border: `1px solid ${XXL_COLORS.primary} !important`,
        boxShadow: 'none',
        '&:hover': {
          backgroundColor: `${XXL_COLORS.accent} !important`,
          color: `${XXL_COLORS.primary} !important`,
          boxShadow: primaryButtonHoverShadow,
        },
      },
      containedSecondary: {
        backgroundColor: `${XXL_COLORS.accent} !important`,
        color: '#000000 !important',
        fontFamily: SPACE_GROTESK_FONT_FAMILY,
        boxShadow: shadowButton,
        '&:hover': {
          backgroundColor: `${XXL_COLORS.accentDark} !important`,
          boxShadow: shadowButtonHover,
        },
      },
      outlined: {
        borderColor: XXL_COLORS.borderStrong,
        color: XXL_COLORS.sectionTitle,
        backgroundColor: XXL_COLORS.paperBackground,
        '&:hover': {
          borderColor: XXL_COLORS.accent,
          backgroundColor: XXL_COLORS.tileSelectedBg,
        },
      },
      text: {
        color: XXL_COLORS.sectionTitle,
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius,
        backgroundColor: XXL_COLORS.inputBackground,
        '& fieldset': {
          borderColor: XXL_COLORS.borderStrong,
        },
        '&:hover fieldset': {
          borderColor: XXL_COLORS.borderStrong,
        },
        '&.Mui-focused fieldset': {
          borderColor: XXL_COLORS.accent,
          borderWidth: '2px',
        },
      },
    },
    MuiInputLabel: {
      root: {
        color: XXL_COLORS.textBase,
        '&.Mui-focused': {
          color: XXL_COLORS.accent,
        },
      },
    },
    MuiFormLabel: {
      root: {
        color: XXL_COLORS.textBase,
      },
    },
    MuiTypography: {
      body1: { color: XXL_COLORS.sectionTitle },
      body2: { color: XXL_COLORS.sectionTitle },
      caption: { color: XXL_COLORS.sectionTitle },
    },
    AmountPrice: {
      root: {
        width: '100% !important',
        position: 'relative',
      },
      summary: {
        display: 'none',
      },
      // Mobile AmountPrice layout
      card: amountPriceMobileCardXs,
      cardUnboxed: amountPriceMobileCardXs,
      AmountPriceContent: {
        [theme.breakpoints.down('xs')]: {
          paddingBottom: 0,
          '& > .MuiGrid-container': {
            flexDirection: 'row',
            flexWrap: 'nowrap',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: theme.spacing(2),
          },
          '& > .MuiGrid-container > .MuiGrid-item': {
            flexBasis: 'auto',
            maxWidth: 'none',
            width: 'auto',
            paddingTop: 0,
            paddingBottom: 0,
          },
        },
      },
      AmountPriceButton: {
        [theme.breakpoints.down('xs')]: {
          padding: theme.spacing(1.25, 2),
          minWidth: 'auto',
          fontSize: '0.8125rem',
          lineHeight: 1.3,
        },
      },
      AmountPriceTypography: {
        [theme.breakpoints.down('xs')]: {
          fontSize: '1.375rem',
          lineHeight: 1.2,
          padding: 0,
          textAlign: 'end',
        },
      },
      AmountPriceTypographyVat: {
        paddingRight: 1,
        [theme.breakpoints.down('xs')]: {
          fontSize: '0.6875rem',
          lineHeight: 1.4,
          marginTop: theme.spacing(0.5),
          textAlign: 'end',
        },
      },
      cardActions: {
        [theme.breakpoints.down('xs')]: {
          padding: 0,
          paddingBottom: 4,
          width: '100%',
          overflow: 'visible',
        },
      },
      mainButton: {
        [theme.breakpoints.down('xs')]: {
          height: 48,
          marginBottom: 0,
          '& .MuiButtonBase-root': { marginBottom: 0 },
        },
      },
    },
  });
};

export default function CustomThemeProvider({ children }) {
  return (
    <ConnectedThemeProvider customTheme={customTheme} withTheme={overrideTheme}>
      <CssBaseline />
      {children}
    </ConnectedThemeProvider>
  );
}

CustomThemeProvider.propTypes = {
  children: PropTypes.node,
};
